// authenticity-capture — code-authenticity-snapshots (task 4). Paste-anchored
// provenance capture for the STUDENT assignment surface ONLY. When the operator flag
// is on (the assignment body carries data-authenticity="true"), this wires:
//
//   * Genuine PASTE capture via Ace's 'paste' event + the following insert delta. A
//     paste of >= ~5 lines OR ~80 chars is FLAGGED and recorded with the full
//     before/after multi-file code state, the pasted text, its location (file + range)
//     for highlighting, and char/line counts.
//   * A LARGE-INSERT backstop on the editor 'change' event: an over-threshold insert
//     that did NOT arrive via a real paste (e.g. a programmatic injection) is recorded
//     with kind:"largeInsert".
//   * Two running session counters — characters entered via paste vs via typing — sent
//     with each record so the teacher summary can compute the share of entered
//     characters that arrived via paste (pasted / (pasted + typed)).
//
// Programmatic content loads (initial load / draft rehydrate / restore / starter code /
// add-rename-delete buffer rebuilds) are EXCLUDED via core.isLoading(): editor-core
// sets that flag around its own staging, so none of those count as pastes/inserts.
//
// Capture must never disrupt editing: record POSTs are fire-and-forget (failures are
// logged, swallowed). Once the per-submission cap is reached the server stops storing
// (it tells us via capReached), and we stop posting to avoid pointless traffic — but
// counters keep running locally so a late record (if any) still reflects them.

// Flag thresholds (memo defaults, fixed in v1): a single paste is flagged at >= 5 lines
// OR >= 80 characters; the large-insert backstop uses the same size threshold.
var FLAG_MIN_LINES = 5;
var FLAG_MIN_CHARS = 80;

// solution-pieces-merge: a one-shot flag set by challenge-pieces.js immediately BEFORE its
// "Merge at cursor" insert and cleared in a `finally` right after it. The very next insert delta
// consumes it: that insert is neither recorded (no paste / largeInsert) nor counted as typed.
// Module-level because the merge and the capture are separate modules sharing one editor.
// Capture is wired only on the assignment surface today, so on challenge this is defence for a
// future wiring.
var solutionPieceInsert = false;

export function markSolutionPieceInsert() { solutionPieceInsert = true; }
export function clearSolutionPieceInsert() { solutionPieceInsert = false; }

function lineCountOf(text) {
  if (!text) { return 0; }
  // An N-newline insert spans N+1 lines; a no-newline insert is 1 line.
  var n = 0, i = -1;
  while ((i = text.indexOf('\n', i + 1)) !== -1) { n++; }
  return n + 1;
}

function isFlagged(text) {
  var chars = text ? text.length : 0;
  return chars >= FLAG_MIN_CHARS || lineCountOf(text) >= FLAG_MIN_LINES;
}

// wire(ctx) — ctx = { core, api, doc, draftTrinketId }. Returns nothing; inert if the
// flag attribute is absent or the editor/trinket id is unavailable.
export function wire(ctx) {
  var document = ctx.doc;
  var core = ctx.core;
  var api = ctx.api;
  var trinketId = ctx.draftTrinketId;

  if (!document || !core || !api || !trinketId) { return; }
  // Server-injected gate: only the assignment student surface, only when the flag is on.
  if (document.body.getAttribute('data-authenticity') !== 'true') { return; }

  var editor = core.getEditor && core.getEditor();
  if (!editor || !editor.on) { return; }

  // Running session counters (design D6 / ledger Gap 2/3).
  var pastedChars = 0;
  var typedChars = 0;

  // Stop posting once the server reports the per-submission cap is reached (counters
  // keep running locally regardless).
  var capReached = false;

  // Set by Ace's 'paste' event immediately BEFORE the insert delta arrives. Carries the
  // before-snapshot captured at paste time so the record has accurate context.
  var pendingPaste = null;

  function snapshot() {
    try { return core.getFiles(); } catch (e) { return []; }
  }

  function post(record) {
    if (capReached) { return; }
    api.recordAuthenticity(trinketId, record)
      .then(function (res) {
        if (res && res.capReached) { capReached = true; }
      })
      .catch(function (err) { console.error(err); }); // non-fatal: never disrupt editing
  }

  // Ace fires 'paste' before applying the clipboard text. Snapshot the BEFORE state now;
  // the matching insert delta (next 'change') carries the exact location + final text.
  editor.on('paste', function (e) {
    if (core.isLoading && core.isLoading()) { return; }
    pendingPaste = {
      before: snapshot(),
      text: (e && typeof e.text === 'string') ? e.text : ''
    };
  });

  // The actual content mutation. Ace deltas: { action: 'insert'|'remove', start, end,
  // lines }. We only care about inserts (typing, paste, programmatic injection).
  editor.on('change', function (delta) {
    if (!delta || delta.action !== 'insert') { return; }
    // Exclude programmatic staging (initial load / rehydrate / restore / file ops).
    if (core.isLoading && core.isLoading()) { pendingPaste = null; solutionPieceInsert = false; return; }
    // A solution-piece merge: consumed once, never flagged, never counted as typed.
    if (solutionPieceInsert) { solutionPieceInsert = false; pendingPaste = null; return; }

    var lines = delta.lines || [];
    var text = lines.join('\n');
    var chars = text.length;
    var loc = {
      file: core.getActiveFile ? (core.getActiveFile() || '') : '',
      startRow: delta.start ? delta.start.row : 0,
      startColumn: delta.start ? delta.start.column : 0,
      endRow: delta.end ? delta.end.row : 0,
      endColumn: delta.end ? delta.end.column : 0
    };

    var paste = pendingPaste;
    pendingPaste = null;

    if (paste) {
      // Genuine paste. Count toward the pasted total regardless of flagging.
      pastedChars += chars;
      if (isFlagged(text)) {
        post({
          kind: 'paste',
          pastedText: text,
          charCount: chars,
          lineCount: lineCountOf(text),
          location: loc,
          before: paste.before,
          after: snapshot(),
          pastedChars: pastedChars,
          typedChars: typedChars
        });
      }
      return;
    }

    // Not a paste. A small insert is ordinary typing -> typed counter. An over-threshold
    // non-paste insert is the large-insert backstop (programmatic injection).
    if (isFlagged(text)) {
      post({
        kind: 'largeInsert',
        pastedText: text,
        charCount: chars,
        lineCount: lineCountOf(text),
        location: loc,
        // No reliable BEFORE snapshot for a backstop insert (no paste pre-event); the
        // AFTER snapshot is what the report highlights.
        after: snapshot(),
        pastedChars: pastedChars,
        typedChars: typedChars
      });
    } else {
      typedChars += chars;
    }
  });
}
